import type Highcharts from "highcharts";

// USAGE: import Highcharts and this init, then call initParentBubbleLabel(Highcharts) once (e.g. in the component that uses the chart).
// Adding `outerSeriesLabel` property to the series options will enable the parent bubble label.

/** Minimum arc radius when bubble is very small. */
const MIN_ARC_RADIUS = 20;

/**
 * Build SVG path "d" for the top half of a circle (arc from left to right over the top).
 * Sweep-flag 1 = positive angle = arc goes over the top in screen coords (y down).
 */
function topArcPathD(cx: number, cy: number, r: number): string {
  return `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
}

export function initParentBubbleLabel(H: typeof Highcharts): void {
  H.addEvent(
    H.Series,
    "afterDrawDataLabels",
    function (
      this: Highcharts.Series & {
        parentNode?: Highcharts.Point;
        customLabel?: Highcharts.SVGElement;
        customLabelPath?: Highcharts.SVGElement;
      },
    ) {
      const userOptions = this.userOptions as unknown as { outerSeriesLabel: boolean };
      if (!userOptions.outerSeriesLabel) {
        return;
      }

      const chart = this.chart;

      if (!this.parentNode?.plotX || chart.options.chart?.type !== "packedbubble") {
        return;
      }

      const parentNode = this.parentNode;
      const cx = (parentNode.plotX ?? 0) + chart.plotLeft;
      const cy = (parentNode.plotY ?? 0) + chart.plotTop;
      const bbox = parentNode.graphic?.getBBox?.();
      const bubbleRadius = bbox ? Math.min(bbox.width, bbox.height) / 2 : 0;
      const arcRadius = Math.max(bubbleRadius - 5, MIN_ARC_RADIUS);
      const pathId = `parent-bubble-arc-${chart.index ?? 0}-${this.index}`;

      const renderer = chart.renderer;

      if (!this.customLabel) {
        // Path in defs for textPath to reference (top arc of circle; sweep 1 = over the top)
        this.customLabelPath = renderer
          .path([
            ["M", cx - arcRadius, cy],
            ["A", arcRadius, arcRadius, 0, 0, 1, cx + arcRadius, cy],
          ])
          .attr({ id: pathId })
          .add(renderer.defs);

        // Group for curved text
        this.customLabel = renderer.g().attr({ zIndex: 3 }).add();

        const textEl = renderer.createElement("text");
        textEl
          .attr({
            textAnchor: "middle",
            fill: "black",
          })
          .css({
            fontSize: "18px",
          });

        const textPathEl = renderer.createElement("textPath");
        (textPathEl.element as SVGTextPathElement).setAttributeNS(
          "http://www.w3.org/1999/xlink",
          "href",
          `#${pathId}`,
        );
        textPathEl.element.textContent = this.name;

        textEl.element.appendChild(textPathEl.element);
        this.customLabel.element.appendChild(textEl.element);
      } else {
        // Update arc path position on redraw (bubble may have moved/resized)
        const pathD = topArcPathD(cx, cy, arcRadius);
        this.customLabelPath?.attr({ d: pathD });
      }

      // Ensure text content is current (name might have changed)
      const textEl = this.customLabel.element.querySelector("text");
      const textPathEl = textEl?.querySelector("textPath");
      if (textPathEl) {
        textPathEl.textContent = this.name;
      }

      // Center the text so its midpoint is at the top of the bubble (50% along the arc)
      const textNode = this.customLabel.element.querySelector("text");
      const pathNode = this.customLabelPath?.element;
      if (textNode && pathNode && "getTotalLength" in pathNode) {
        const pathLength = (pathNode as SVGPathElement).getTotalLength();
        const textLength = (textNode as SVGTextContentElement).getComputedTextLength();
        const startOffset = Math.max(0, (pathLength - textLength) / 2);
        const tp = textNode.querySelector("textPath");
        if (tp) {
          tp.setAttribute("startOffset", String(startOffset));
        }
      }
    },
  );
}
